/**
 * ranking.js — Classement annuel des clubs par PROGRESSION (pur, sans React/fetch).
 *
 * Objectif métier : valoriser les clubs qui progressent le plus par rapport à leur
 * propre point de départ, et non ceux qui ont les plus gros chiffres absolus.
 *   Ex. 1 000 → 1 500 abonnés = +50 %  bat  10 000 → 12 000 = +20 %.
 *
 * Trois familles d'indicateurs (METRIC_KIND) :
 *   - stock  (abonnés)               : valeur à un instant T. Progression = (fin-début)/début.
 *   - flow   (portée, vues, interactions) : cumul sur une période. Progression = année N vs N-1
 *                                      (même fenêtre de mois), comme validé avec le client.
 *   - ratio  (engagement)            : pourcentage. Progression exprimée en POINTS (fin-début),
 *                                      + variation relative en complément.
 *
 * Politique anti-classement-trompeur :
 *   - base nulle/quasi nulle            → non-classé ("base nulle"), pas de +∞ %.
 *   - démarrage en cours d'année        → provisoire (hors classement).
 *   - historique insuffisant            → non-classé.
 *   - année en cours (pas encore finie) → classé mais marqué "provisoire".
 *   - France (agrégat national)         → jamais classée face aux clubs.
 */

export const METRIC_KIND = {
  abonnes: "stock",
  followers: "stock",
  portee: "flow",
  porteeMoy: "flow",
  reach: "flow",
  vues: "flow",
  views: "flow",
  interactions: "flow",
  engagement: "ratio",
};

export const POLICY = {
  minCoverageStock: 2, // ≥2 points pour afficher une progression stock
  minCoverageFlow: 2, // ≥2 mois dans la fenêtre N pour un flux
  anchorToleranceMonths: 2, // l'ancre de départ doit tomber dans les 2 premiers mois de l'année
  zeroBaselineEps: 1e-9,
};

const round = (v, d = 2) => (v == null || isNaN(v) ? null : Number(Number(v).toFixed(d)));
const monthNum = (key) => Number(String(key).split("-")[1]); // "2026-03" -> 3

export function metricKind(indicator) {
  return METRIC_KIND[indicator] || "flow";
}

/**
 * Calcule la progression d'un club pour un indicateur sur une année.
 *
 * @param {object} p
 * @param {string|number} p.clubId
 * @param {string} p.indicator           clé indicateur (abonnes, portee, vues, interactions, engagement…)
 * @param {string} [p.kind]              stock|flow|ratio (déduit sinon)
 * @param {number} p.year                année cible (N)
 * @param {Array<{month:string,value:number|null,available:boolean}>} p.points  séries mensuelles de l'année N, triées asc.
 * @param {Array<{month:string,value:number|null,available:boolean}>} [p.prevYearPoints] séries de l'année N-1 (flux).
 * @param {boolean} [p.yearComplete]     true si l'année est terminée (sinon → provisoire).
 * @returns {object} growth
 */
export function computeClubGrowth({
  clubId,
  indicator,
  kind,
  year,
  points = [],
  prevYearPoints = [],
  yearComplete = false,
}) {
  const k = kind || metricKind(indicator);
  const avail = points.filter((p) => p && p.available && p.value != null && !isNaN(p.value));
  const coverageMonths = avail.length;

  const base = {
    clubId,
    indicator,
    kind: k,
    year,
    baseline: null,
    current: null,
    evolutionAbs: null,
    evolutionPct: null,
    evolutionPoints: null,
    coverageMonths,
    provisional: false,
    status: "non-classé",
    reason: null,
  };

  if (coverageMonths === 0) {
    return { ...base, reason: "aucune donnée" };
  }

  const first = avail[0];
  const last = avail[avail.length - 1];
  const startedMidYear = monthNum(first.month) > POLICY.anchorToleranceMonths + 0;
  const provisional = !yearComplete;

  // ── STOCK (abonnés) : début → fin d'année ──────────────────────────────
  if (k === "stock") {
    if (coverageMonths < POLICY.minCoverageStock) {
      return { ...base, reason: "historique insuffisant" };
    }
    const baseline = first.value;
    const current = last.value;
    const evolutionAbs = current - baseline;
    if (Math.abs(baseline) <= POLICY.zeroBaselineEps) {
      return {
        ...base,
        baseline,
        current,
        evolutionAbs,
        provisional,
        status: "non-classé",
        reason: "base nulle",
      };
    }
    const evolutionPct = round((evolutionAbs / baseline) * 100);
    const status = startedMidYear ? "provisoire" : provisional ? "classé" : "classé";
    return {
      ...base,
      baseline: round(baseline),
      current: round(current),
      evolutionAbs: round(evolutionAbs),
      evolutionPct,
      provisional: provisional || startedMidYear,
      status: startedMidYear ? "provisoire" : "classé",
      reason: startedMidYear ? "démarrage en cours d'année" : null,
    };
  }

  // ── RATIO (engagement) : points de % début → fin ───────────────────────
  if (k === "ratio") {
    if (coverageMonths < POLICY.minCoverageStock) {
      return { ...base, reason: "historique insuffisant" };
    }
    const baseline = first.value;
    const current = last.value;
    const evolutionPoints = round(current - baseline); // points de pourcentage (primaire)
    const evolutionPct =
      Math.abs(baseline) <= POLICY.zeroBaselineEps ? null : round(((current - baseline) / baseline) * 100);
    return {
      ...base,
      baseline: round(baseline),
      current: round(current),
      evolutionAbs: evolutionPoints,
      evolutionPoints,
      evolutionPct,
      provisional: provisional || startedMidYear,
      status: startedMidYear ? "provisoire" : "classé",
      reason: startedMidYear ? "démarrage en cours d'année" : null,
    };
  }

  // ── FLOW (portée, vues, interactions) : année N vs N-1, même fenêtre ────
  const availPrev = (prevYearPoints || []).filter(
    (p) => p && p.available && p.value != null && !isNaN(p.value)
  );
  // Fenêtre = mois présents en N ; on exige les mêmes mois en N-1.
  const monthsN = new Set(avail.map((p) => monthNum(p.month)));
  const windowPrev = availPrev.filter((p) => monthsN.has(monthNum(p.month)));
  const currentSum = avail.reduce((t, p) => t + Number(p.value || 0), 0);

  if (coverageMonths < POLICY.minCoverageFlow) {
    return { ...base, current: round(currentSum), reason: "historique insuffisant" };
  }
  if (windowPrev.length < coverageMonths) {
    // N-1 incomplet sur la même fenêtre → progression non fiable
    return {
      ...base,
      current: round(currentSum),
      provisional: true,
      status: "provisoire",
      reason: "année N-1 incomplète",
    };
  }
  const baselineSum = windowPrev.reduce((t, p) => t + Number(p.value || 0), 0);
  const evolutionAbs = currentSum - baselineSum;
  if (Math.abs(baselineSum) <= POLICY.zeroBaselineEps) {
    return {
      ...base,
      baseline: round(baselineSum),
      current: round(currentSum),
      evolutionAbs: round(evolutionAbs),
      status: "non-classé",
      reason: "base nulle",
    };
  }
  return {
    ...base,
    baseline: round(baselineSum),
    current: round(currentSum),
    evolutionAbs: round(evolutionAbs),
    evolutionPct: round((evolutionAbs / baselineSum) * 100),
    provisional,
    status: startedMidYear ? "provisoire" : "classé",
    reason: startedMidYear ? "démarrage en cours d'année" : null,
  };
}

/**
 * Trie et classe un ensemble de progressions clubs.
 * La clé de tri est evolutionPoints pour les ratios, evolutionPct sinon.
 *
 * @param {object} p
 * @param {Array<object>} p.perClubGrowth  résultats de computeClubGrowth (1 par club).
 * @param {string} [p.kind]
 * @returns {{ ranked: Array, unranked: Array, sortKey: string }}
 */
export function computeGrowthRanking({ perClubGrowth = [], kind }) {
  const k = kind || perClubGrowth[0]?.kind || "flow";
  const sortKey = k === "ratio" ? "evolutionPoints" : "evolutionPct";

  const classe = perClubGrowth.filter((g) => g.status === "classé" && g[sortKey] != null);
  const reste = perClubGrowth.filter((g) => g.status !== "classé" || g[sortKey] == null);

  const ranked = classe
    .slice()
    .sort((a, b) => Number(b[sortKey]) - Number(a[sortKey]))
    .map((g, i) => ({ ...g, rank: i + 1 }));

  // Hors classement : ordonnés par couverture décroissante pour une lecture stable.
  const unranked = reste.slice().sort((a, b) => (b.coverageMonths || 0) - (a.coverageMonths || 0));

  return { ranked, unranked, sortKey };
}

/**
 * Classement global pondéré (2e temps — méthode à valider).
 * Normalise chaque evolutionPct entre clubs (min-max → 0..100) puis applique des poids.
 * Non branché par défaut dans l'UI ; exposé pour une itération ultérieure.
 */
export function computeWeightedRanking({ perClubByIndicator = {}, weights = {} } = {}) {
  const indicators = Object.keys(perClubByIndicator);
  const clubIds = new Set();
  indicators.forEach((ind) =>
    (perClubByIndicator[ind] || []).forEach((g) => clubIds.add(String(g.clubId)))
  );

  const normById = {};
  indicators.forEach((ind) => {
    const rows = (perClubByIndicator[ind] || []).filter((g) => g.evolutionPct != null);
    const vals = rows.map((g) => g.evolutionPct);
    const min = Math.min(...vals, 0);
    const max = Math.max(...vals, 0.01);
    rows.forEach((g) => {
      const n = max === min ? 50 : ((g.evolutionPct - min) / (max - min)) * 100;
      (normById[String(g.clubId)] ||= {})[ind] = n;
    });
  });

  const totalWeight = indicators.reduce((t, ind) => t + (weights[ind] || 1), 0) || 1;
  const scored = [...clubIds].map((clubId) => {
    const per = normById[clubId] || {};
    const score =
      indicators.reduce((t, ind) => t + (per[ind] || 0) * (weights[ind] || 1), 0) / totalWeight;
    return { clubId, score: round(score), perIndicator: per };
  });
  return scored.sort((a, b) => b.score - a.score).map((s, i) => ({ ...s, rank: i + 1 }));
}
